import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ProjectConfig,
  ProjectConfigInput,
  ProjectConfigListQueryInput,
  ProjectConfigUpdateInput,
} from '@cms/models';
import apiClient from './axios';

export const projectConfigsQueryKeys = {
  all: ['project-configs'] as const,
  list: (filters?: ProjectConfigListQueryInput) =>
    ['project-configs', 'list', filters ?? {}] as const,
};

const BASE = '/project-configs';

export const projectConfigsApi = {
  async list(filters?: ProjectConfigListQueryInput): Promise<ProjectConfig[]> {
    const params: Record<string, string> = {};
    if (filters?.status) params.status = filters.status;
    if (filters?.type) params.type = filters.type;
    if (filters?.search?.trim()) params.search = filters.search.trim();
    const { data } = await apiClient.get<ProjectConfig[]>(BASE, { params });
    return Array.isArray(data) ? data : [];
  },

  async create(payload: ProjectConfigInput): Promise<ProjectConfig> {
    const { data } = await apiClient.post<ProjectConfig>(BASE, payload);
    return data;
  },

  async update(id: string, payload: ProjectConfigUpdateInput): Promise<ProjectConfig> {
    const { data } = await apiClient.patch<ProjectConfig>(`${BASE}/${id}`, payload);
    return data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },
};

export function useProjectConfigs(filters?: ProjectConfigListQueryInput) {
  return useQuery({
    queryKey: projectConfigsQueryKeys.list(filters),
    queryFn: () => projectConfigsApi.list(filters),
  });
}

export function useCreateProjectConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProjectConfigInput) => projectConfigsApi.create(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: projectConfigsQueryKeys.all });
    },
  });
}

export function useUpdateProjectConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: string; payload: ProjectConfigUpdateInput }) =>
      projectConfigsApi.update(params.id, params.payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: projectConfigsQueryKeys.all });
    },
  });
}

export function useDeleteProjectConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => projectConfigsApi.delete(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: projectConfigsQueryKeys.all });
    },
  });
}
